import { useState, useEffect, useMemo } from 'react';
import { X, ChevronDown } from 'lucide-react';
import { useSpecHistory, useSpecDiff } from '../../hooks/useSpecifications';
import { DiffField } from '../spec/SpecDiffView';
import Spinner from '../ui/Spinner';
import type { NeedsReviewGroup } from '../../services/testCaseService';

interface SpecChangeHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  groups: NeedsReviewGroup[];
  onSelectTC?: (testCaseId: string) => void;
}

export default function SpecChangeHistoryModal({
  isOpen,
  onClose,
  projectId,
  groups,
  onSelectTC,
}: SpecChangeHistoryModalProps) {
  const [selectedSpecId, setSelectedSpecId] = useState<string>(groups[0]?.specId || '');

  // 선택 변경 시 specId 동기화
  useEffect(() => {
    if (groups.length > 0 && !groups.find((g) => g.specId === selectedSpecId)) {
      setSelectedSpecId(groups[0].specId);
    }
  }, [groups, selectedSpecId]);

  const selectedGroup = groups.find((g) => g.specId === selectedSpecId);

  // 버전 비교 상태
  const [v1, setV1] = useState(0);
  const [v2, setV2] = useState(0);

  // selectedSpecId 변경 시 버전 초기화
  useEffect(() => {
    if (selectedGroup) {
      setV1(selectedGroup.previousVersion);
      setV2(selectedGroup.latestVersion);
    }
  }, [selectedSpecId]); // eslint-disable-line react-hooks/exhaustive-deps

  // History 목록 조회 (버전 선택 드롭다운용)
  const { data: history, isLoading: historyLoading } = useSpecHistory(
    projectId,
    selectedSpecId,
  );

  // Diff 데이터 조회
  const { data: diffResult, isLoading: diffLoading } = useSpecDiff(
    projectId,
    selectedSpecId,
    v1,
    v2,
  );

  const changedDiffs = useMemo(
    () => diffResult?.diffs.filter((d) => d.type !== 'unchanged') ?? [],
    [diffResult],
  );

  // 버전 옵션 목록 (현재 버전은 History에 없으므로 수동 추가)
  const versionOptions = useMemo(() => {
    const options: { value: number; label: string }[] = [];
    if (history) {
      for (const h of history) {
        const date = new Date(h.changedAt).toLocaleString('ko-KR', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
        options.push({ value: h.version, label: `v${h.version} — ${h.changer.name} (${date})` });
      }
    }
    // 현재 버전(Specification.version)이 history에 없으면 추가
    if (selectedGroup) {
      const currentVer = selectedGroup.latestVersion;
      if (!options.some((o) => o.value === currentVer)) {
        options.unshift({ value: currentVer, label: `v${currentVer} (현재)` });
      }
    }
    return options;
  }, [history, selectedGroup]);

  if (!isOpen || groups.length === 0) return null;

  const isSingleSpec = groups.length === 1;
  const isLoading = historyLoading || diffLoading;

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col">
        {/* 헤더 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">기능명세 변경이력</h3>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 transition-colors"
          >
            <X className="size-5 text-gray-500" />
          </button>
        </div>

        {/* 본문 */}
        <div className="flex-1 overflow-hidden flex min-h-0">
          {/* 좌측: 명세 목록 (다수 명세일 때만) */}
          {!isSingleSpec && (
            <div className="w-64 border-r border-gray-200 overflow-y-auto bg-gray-50 p-4 flex-shrink-0">
              <h4 className="text-sm font-medium text-gray-500 mb-3">
                변경된 명세 ({groups.length}건)
              </h4>
              <div className="space-y-2">
                {groups.map((group) => (
                  <button
                    key={group.specId}
                    onClick={() => setSelectedSpecId(group.specId)}
                    className={`w-full text-left p-3 rounded-lg text-sm transition-colors ${
                      selectedSpecId === group.specId
                        ? 'bg-blue-100 border border-blue-300'
                        : 'bg-white border border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    <div className="font-medium text-gray-900">{group.functionCode}</div>
                    <div className="text-gray-600 truncate">{group.specTitle}</div>
                    <div className="text-sm text-gray-400 mt-1">TC {group.testCaseCount}건</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 우측: Diff 영역 */}
          <div className="flex-1 overflow-y-auto p-6 min-w-0">
            {selectedGroup && (
              <>
                {/* 명세 정보 */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-mono text-gray-500">
                      {selectedGroup.functionCode}
                    </span>
                    <span className="font-medium text-gray-900">{selectedGroup.specTitle}</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span>변경자: {selectedGroup.lastChangedBy?.name || '-'}</span>
                    <span>변경일: {formatDateTime(selectedGroup.lastChangedAt)}</span>
                    <span>관련 TC: {selectedGroup.testCaseCount}건</span>
                  </div>
                </div>

                {/* 버전 비교 선택 */}
                <div className="mb-4 flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-3 border border-gray-200">
                  <span className="text-sm font-medium text-gray-600">버전 비교:</span>
                  <div className="relative">
                    <select
                      value={v1}
                      onChange={(e) => setV1(Number(e.target.value))}
                      className="appearance-none pl-3 pr-8 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white"
                    >
                      <option value={0}>선택...</option>
                      {versionOptions.map((o) => (
                        <option key={`v1-${o.value}`} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 size-4 text-gray-400 pointer-events-none" />
                  </div>
                  <span className="text-gray-400">→</span>
                  <div className="relative">
                    <select
                      value={v2}
                      onChange={(e) => setV2(Number(e.target.value))}
                      className="appearance-none pl-3 pr-8 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white"
                    >
                      <option value={0}>선택...</option>
                      {versionOptions.map((o) => (
                        <option key={`v2-${o.value}`} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 size-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                {/* Diff 영역 */}
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Spinner className="size-6" />
                  </div>
                ) : v1 === 0 || v2 === 0 || v1 === v2 ? (
                  <div className="flex items-center justify-center py-12 text-gray-400 text-sm">
                    비교할 두 버전을 선택해주세요.
                  </div>
                ) : changedDiffs.length === 0 ? (
                  <div className="flex items-center justify-center py-12 text-gray-400 text-sm">
                    두 버전 간 변경 사항이 없습니다.
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-500">
                      {changedDiffs.length}개 필드가 변경되었습니다.
                    </p>
                    {changedDiffs.map((diff) => (
                      <DiffField key={diff.field} diff={diff} />
                    ))}
                  </div>
                )}

                {/* 관련 TC 목록 */}
                <div className="mt-6 border-t border-gray-200 pt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    관련 테스트케이스 ({selectedGroup.testCaseCount}건)
                  </h4>
                  <div className="space-y-1">
                    {selectedGroup.testCases.map((tc) => (
                      <button
                        key={tc.testCaseId}
                        onClick={() => {
                          onClose();
                          onSelectTC?.(tc.testCaseId);
                        }}
                        className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-gray-50 flex items-center justify-between transition-colors"
                      >
                        <span>
                          <span className="font-mono text-gray-500 mr-2">{tc.testCaseKey}</span>
                          <span className="text-gray-900">{tc.title}</span>
                        </span>
                        <span className="text-sm px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full flex-shrink-0">
                          재검토 필요
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* 푸터 */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}
